// src/features/system/developer/components/video-gallery/hooks/useWakeLock.ts
// ────────────────────────────────────────────────────────────────────────────
// Screen Wake Lock API integration (TikTok / YouTube Shorts parity).
//
// While a video is actively playing on the active slide, request a screen
// wake lock so the device doesn't dim or auto-lock mid-playback. The lock
// is released automatically when:
//   • the slide is no longer active
//   • playback pauses / ends
//   • the tab becomes hidden (browser also auto-releases on visibility)
//   • the component unmounts
//
// Single-flight: only one outstanding request per hook instance. Graceful
// no-op on browsers without the API (older Safari, Firefox < 126) — never
// throws, never warns.
// ────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';

// Minimal local typings (DOM lib doesn't include WakeLock until TS 5.4+).
interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
  removeEventListener: (type: 'release', listener: () => void) => void;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as unknown as { wakeLock?: WakeLockLike };
  return nav.wakeLock ?? null;
}

/**
 * Acquire a screen wake lock while `shouldHold` is true. Released
 * deterministically as soon as the condition flips false or the
 * component unmounts.
 */
export function useWakeLock(shouldHold: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const acquiringRef = useRef(false);

  useEffect(() => {
    const api = getWakeLock();
    if (!api) return; // Unsupported browser — silent no-op.

    let cancelled = false;

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s && !s.released) {
        try { await s.release(); } catch { /* ignore */ }
      }
    };

    const acquire = async () => {
      if (acquiringRef.current || sentinelRef.current) return;
      acquiringRef.current = true;
      try {
        const s = await api.request('screen');
        if (cancelled) {
          try { await s.release(); } catch { /* ignore */ }
          return;
        }
        sentinelRef.current = s;
        // Browser may auto-release on visibility change; clean up our ref
        // so the next `acquire()` will re-request cleanly.
        s.addEventListener('release', () => {
          if (sentinelRef.current === s) sentinelRef.current = null;
        });
      } catch {
        // NotAllowedError, AbortError, etc. — wake lock is a hint, never
        // critical; failure is fine.
      } finally {
        acquiringRef.current = false;
      }
    };

    if (shouldHold) {
      void acquire();
    } else {
      void release();
    }

    return () => {
      cancelled = true;
      void release();
    };
  }, [shouldHold]);
}
